// frontend/src/components/common/AttachmentUploader.jsx
// Selector de archivos múltiple + lista de adjuntos ya subidos, con
// posibilidad de eliminar cada uno individualmente. Extraído del patrón
// AttachmentBox usado en AlmacenPage.jsx para reutilizarlo en Documentación.
import React from 'react';
import styled from 'styled-components';

const AttachmentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px dashed ${({ theme, $invalid }) => ($invalid ? theme.colors.danger : theme.colors.borderDefault)};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.bgPrincipal};
`;

const AttachmentButtonLabel = styled.label`
  width: fit-content;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: 8px;
  padding: 0 12px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  ${({ $disabled }) => $disabled && `
    opacity: 0.6;
    pointer-events: none;
  `}
`;

const AttachmentInput = styled.input`
  display: none;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AttachmentChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 0.8rem;
`;

const ChipLink = styled.a`
  color: ${({ theme }) => theme.colors.txtPrincipal};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0;
  font-size: 0.85rem;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.danger};
  }
`;

const HelperText = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/**
 * existingFiles: [{ id, archivo_url, archivo_nombre_original }]
 * pendingFiles: [File, ...] (aún no subidos)
 */
const AttachmentUploader = ({
  label = 'Adjuntar archivos',
  helperText,
  accept,
  existingFiles = [],
  pendingFiles = [],
  onFilesSelected,
  onRemoveExisting,
  onRemovePending,
  disabled = false,
  invalid = false,
}) => {
  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && typeof onFilesSelected === 'function') {
      onFilesSelected(files);
    }
    e.target.value = '';
  };

  return (
    <AttachmentBox $invalid={invalid}>
      <AttachmentButtonLabel $disabled={disabled}>
        <i className="bi bi-paperclip"></i>
        {label}
        <AttachmentInput
          type="file"
          accept={accept}
          multiple
          disabled={disabled}
          onChange={handleChange}
        />
      </AttachmentButtonLabel>

      {helperText && <HelperText>{helperText}</HelperText>}

      {existingFiles.length > 0 && (
        <AttachmentList>
          {existingFiles.map((adj) => (
            <AttachmentChip key={adj.id}>
              <ChipLink href={adj.archivo_url || adj.archivo} target="_blank" rel="noopener noreferrer">
                <i className="bi bi-paperclip"></i>
                {adj.archivo_nombre_original || `Adjunto ${adj.id}`}
              </ChipLink>
              {typeof onRemoveExisting === 'function' && (
                <RemoveButton
                  type="button"
                  title="Eliminar adjunto"
                  onClick={() => onRemoveExisting(adj.id)}
                >
                  <i className="bi bi-x-lg"></i>
                </RemoveButton>
              )}
            </AttachmentChip>
          ))}
        </AttachmentList>
      )}

      {pendingFiles.length > 0 && (
        <AttachmentList>
          {pendingFiles.map((file, idx) => (
            <AttachmentChip key={`${file.name}-${file.size}-${idx}`}>
              <i className="bi bi-paperclip"></i>
              {file.name}
              {typeof onRemovePending === 'function' && (
                <RemoveButton tabIndex={-1}
                  type="button"
                  title="Quitar de la selección"
                  onClick={() => onRemovePending(idx)}
                >
                  <i className="bi bi-x-lg"></i>
                </RemoveButton>
              )}
            </AttachmentChip>
          ))}
        </AttachmentList>
      )}
    </AttachmentBox>
  );
};

export default AttachmentUploader;
